//! Programmatic entry point for the import-collision prompt: the dialog shown
//! when importing a file at the cursor would land on cells that already hold
//! content. Callers await `promptImportCollision` and pass the answer straight
//! through to core as an `ImportCollision`, which resolves it against the
//! import's exact output rect.
//!
//! An `ImportCollisionHost` mounted by the sheet UI registers the actual dialog
//! opener. When no host is mounted the import proceeds unchanged, so callers
//! outside the sheet never have to care.

import type { ImportCollision } from '@/app/web-workers/quadraticCore/coreClientMessages';

export type ImportCollisionChoice = ImportCollision | 'cancel';

export type ImportCollisionPromptOptions = {
  /** Shown in the dialog so the user knows which import is asking. */
  fileName: string;
};

type ImportCollisionOpener = (options: ImportCollisionPromptOptions) => Promise<ImportCollisionChoice>;

let opener: ImportCollisionOpener | null = null;

/** Called by `ImportCollisionHost` on mount/unmount. */
export const registerImportCollisionPrompt = (fn: ImportCollisionOpener): (() => void) => {
  opener = fn;
  return () => {
    if (opener === fn) opener = null;
  };
};

/**
 * Asks the user what to do about an import that would cover existing cells.
 * Resolves to `'ignore'` when no host is mounted, leaving the import to behave
 * as it would without the prompt.
 */
export const promptImportCollision = (options: ImportCollisionPromptOptions): Promise<ImportCollisionChoice> => {
  if (opener) return opener(options);
  return Promise.resolve('ignore');
};
